import * as React from "react";

// --- UI Primitives ---
import { Button, ButtonProps } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { type Editor } from "@tiptap/react";

export type FontSizeType = {
  label: string;
  value: string;
};

export const fontSizeOptions: FontSizeType[] = [
  { label: "8px", value: "8px" },
  { label: "10px", value: "10px" },
  { label: "12px", value: "12px" },
  { label: "14px", value: "14px" },
  { label: "16px", value: "16px" },
  { label: "18px", value: "18px" },
  { label: "20px", value: "20px" },
  { label: "24px", value: "24px" },
  { label: "28px", value: "28px" },
  { label: "32px", value: "32px" },
  { label: "36px", value: "36px" },
  { label: "48px", value: "48px" },
  { label: "56px", value: "56px" },
  { label: "64px", value: "64px" },
  { label: "72px", value: "72px" },
  { label: "96px", value: "96px" },
  { label: "128px", value: "128px" },
];

export interface FontSizeButtonProps extends Omit<ButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * The font size value to apply.
   */
  fontSize: string;
  /**
   * The display text for the button.
   */
  text: string;
  /**
   * Whether the button should be hidden when unavailable.
   * @default false
   */
  hideWhenUnavailable?: boolean;
}

export function canSetFontSize(editor: Editor | null, fontSize: string): boolean {
  if (!editor) return false;
  // Check if the editor can set the textStyle mark
  try {
    return editor.can().setMark("textStyle", { fontSize });
  } catch {
    return false;
  }
}

export function isFontSizeActive(editor: Editor | null, fontSize: string): boolean {
  if (!editor) return false;
  // Check if the textStyle mark is active with the specific fontSize
  return editor.isActive("textStyle", { fontSize });
}

export const FontSizeButton = React.forwardRef<HTMLButtonElement, FontSizeButtonProps>(
  ({ editor: providedEditor, fontSize, text, hideWhenUnavailable = false, ...props }, ref) => {
    const editor = useTiptapEditor(providedEditor);

    const canToggle = React.useMemo(() => canSetFontSize(editor, fontSize), [editor, fontSize]);
    const isActive = React.useMemo(() => isFontSizeActive(editor, fontSize), [editor, fontSize]);

    const handleClick = React.useCallback(() => {
      if (editor && canToggle) {
        editor.commands.setMark("textStyle", { fontSize });
      }
    }, [editor, canToggle, fontSize]);

    if (hideWhenUnavailable && !canToggle) {
      return null;
    }

    return (
      <Button
        type="button"
        data-style="ghost"
        data-active-state={isActive ? "on" : "off"}
        role="button"
        aria-label={text}
        aria-pressed={isActive}
        tooltip={text}
        {...props}
        onClick={handleClick}
        ref={ref}
      >
        <>{text && <span className="tiptap-button-text">{text}</span>}</>
      </Button>
    );
  },
);

FontSizeButton.displayName = "FontSizeButton";

export default FontSizeButton;
